import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Clock, MapPin, Loader2, Zap, CheckCircle, XCircle, ReceiptText } from "lucide-react";

const regStatusStyle = { REGISTERED: "status-confirmed", PAID: "status-confirmed", PENDING_PAYMENT: "status-pending", CANCELLED: "status-cancelled" };
const regStatusLabel = { REGISTERED: "Registered", PAID: "Paid", PENDING_PAYMENT: "Awaiting Pay", CANCELLED: "Cancelled" };

export default function MyEvents() {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events/my-registrations").then(r => { setRegs(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const active = regs.filter(r => r.status !== "CANCELLED" && r.event_status !== "CANCELLED");
  const past = regs.filter(r => r.status === "CANCELLED" || r.event_status === "CANCELLED");

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  const RegCard = ({ r }) => (
    <div className="bg-white border-2 border-slate-100 p-4 flex flex-col md:flex-row md:items-center gap-4 hover:border-slate-200 transition-colors cursor-pointer"
      onClick={() => navigate(`/events/${r.event_id}`)} data-testid={`my-event-${r.registration_id}`}>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-base font-bold uppercase tracking-tight">{r.event_title}</h3>
          <Badge className={`${regStatusStyle[r.status]} text-[10px] font-heading uppercase`}>{regStatusLabel[r.status] || r.status}</Badge>
        </div>
        <p className="text-sm text-muted-foreground capitalize">{r.sport_type}</p>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{r.event_date}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.start_time} - {r.end_time}</span>
          {r.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.venue_name}</span>}
        </div>
      </div>
      <div className="text-right">
        <p className="font-heading text-lg font-bold text-court-orange">
          {r.amount > 0 ? `₱${r.amount?.toFixed(0)}` : "FREE"}
        </p>
        {r.status === "PAID" && r.amount > 0 && (
          <Button size="sm" variant="outline" className="rounded-full mt-2 text-xs border-2" data-testid={`event-receipt-${r.registration_id}`} onClick={(e) => { e.stopPropagation(); navigate(`/receipts/event/${r.registration_id}`); }}>
            <ReceiptText className="w-3 h-3 mr-1" />Receipt
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6 flex items-center gap-3" data-testid="my-events-title">
        <Zap className="w-7 h-7 text-court-orange" />My Events
      </h1>

      <Tabs defaultValue="active">
        <TabsList className="mb-6 bg-slate-100 rounded-none h-11">
          <TabsTrigger data-testid="tab-active-events" value="active" className="rounded-none font-heading uppercase tracking-wide data-[state=active]:bg-white">
            Active ({active.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-past-events" value="past" className="rounded-none font-heading uppercase tracking-wide data-[state=active]:bg-white">
            Cancelled ({past.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {active.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-200">
              <Zap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-heading text-lg text-muted-foreground uppercase">No active registrations</p>
              <Button data-testid="browse-events-btn" onClick={() => navigate("/events")} className="mt-4 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase">
                Browse Events
              </Button>
            </div>
          ) : (
            <div className="space-y-3 stagger-in">{active.map(r => <RegCard key={r.registration_id} r={r} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="past">
          {past.length === 0 ? (
            <div className="text-center py-16"><p className="font-heading text-lg text-muted-foreground uppercase">No cancelled registrations</p></div>
          ) : (
            <div className="space-y-3">{past.map(r => <RegCard key={r.registration_id} r={r} />)}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
